import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.snow.css';

/** Tooltips nativos (`title`) na barra do Quill (pt-BR). */
function applyQuillToolbarTitles(root: HTMLElement) {
  const toolbar = root.querySelector('.ql-toolbar');
  if (!toolbar) return;

  toolbar.querySelectorAll('button').forEach((node) => {
    const btn = node as HTMLButtonElement;
    const val = btn.getAttribute('value') ?? '';

    if (btn.classList.contains('ql-bold')) btn.title = 'Negrito';
    else if (btn.classList.contains('ql-italic')) btn.title = 'Itálico';
    else if (btn.classList.contains('ql-strike')) btn.title = 'Texto riscado';
    else if (btn.classList.contains('ql-blockquote')) btn.title = 'Citação em bloco';
    else if (btn.classList.contains('ql-link')) btn.title = 'Inserir ou editar link';
    else if (btn.classList.contains('ql-clean')) btn.title = 'Limpar formatação';
    else if (btn.classList.contains('ql-list')) {
      if (val === 'ordered') btn.title = 'Lista numerada';
      else if (val === 'bullet') btn.title = 'Lista com marcadores';
      else {
        const lists = [...toolbar.querySelectorAll('button.ql-list')];
        const i = lists.indexOf(btn);
        btn.title = i === 0 ? 'Lista numerada' : 'Lista com marcadores';
      }
    } else if (btn.classList.contains('ql-align')) {
      const map: Record<string, string> = {
        '': 'Alinhar à esquerda',
        center: 'Alinhar ao centro',
        right: 'Alinhar à direita',
        justify: 'Justificar',
      };
      if (map[val] !== undefined) btn.title = map[val];
      else {
        const aligns = [...toolbar.querySelectorAll('button.ql-align')];
        const labels = ['Alinhar à esquerda', 'Alinhar ao centro', 'Alinhar à direita', 'Justificar'];
        const j = aligns.indexOf(btn);
        btn.title = j >= 0 ? labels[j] ?? 'Alinhamento' : 'Alinhamento';
      }
    } else if (btn.classList.contains('ql-indent')) {
      if (val === '-1') btn.title = 'Diminuir recuo';
      else if (val === '+1') btn.title = 'Aumentar recuo';
      else {
        const indents = [...toolbar.querySelectorAll('button.ql-indent')];
        const k = indents.indexOf(btn);
        btn.title = k === 0 ? 'Diminuir recuo' : 'Aumentar recuo';
      }
    }
  });

  toolbar.querySelectorAll('.ql-picker').forEach((node) => {
    const picker = node as HTMLElement;
    const label = picker.querySelector('.ql-picker-label') as HTMLElement | null;
    const target = label ?? picker;
    if (picker.classList.contains('ql-header')) target.title = 'Parágrafo ou nível de título (H1, H2, H3)';
    else if (picker.classList.contains('ql-color')) target.title = 'Cor do texto';
    else if (picker.classList.contains('ql-background')) target.title = 'Realce / cor de fundo do texto';
  });
}

const FORMATS = [
  'header',
  'bold',
  'italic',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'align',
  'color',
  'background',
];

function toolbarRows() {
  return [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }, 'blockquote'],
    [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
    ['link'],
    [{ color: [] }, { background: [] }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ];
}

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  id?: string;
};

/**
 * Editor rico para a descrição do estabelecimento (estilo WordPress: parágrafo/títulos, listas, alinhamento, links, cores).
 * Aba «Código» edita o HTML bruto.
 */
export function EstabelecimentoDescricaoEditor({ value, onChange, placeholder, id }: Props) {
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const quillWrapRef = useRef<HTMLDivElement>(null);
  const modules = useMemo(
    () => ({
      toolbar: { container: toolbarRows() },
      history: { delay: 800, maxStack: 100, userOnly: false },
    }),
    []
  );

  useLayoutEffect(() => {
    if (mode !== 'visual') return;
    let cancelled = false;
    const run = () => {
      if (cancelled || !quillWrapRef.current) return;
      applyQuillToolbarTitles(quillWrapRef.current);
    };
    run();
    const t0 = window.setTimeout(run, 0);
    const t1 = window.setTimeout(run, 120);
    return () => {
      cancelled = true;
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [mode, value]);

  return (
    <div className="rounded-lg border border-gray-300 bg-white overflow-hidden estab-descricao-editor">
      <div className="flex items-center justify-end gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <button
          type="button"
          id={id ? `${id}-tab-visual` : undefined}
          onClick={() => setMode('visual')}
          title="Editar com formatação (negrito, listas, etc.)"
          className={`rounded px-3 py-1 text-xs font-medium ${
            mode === 'visual' ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Visual
        </button>
        <button
          type="button"
          id={id ? `${id}-tab-code` : undefined}
          onClick={() => setMode('code')}
          title="Editar o HTML da descrição diretamente"
          className={`rounded px-3 py-1 text-xs font-medium ${
            mode === 'code' ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Código
        </button>
      </div>
      {mode === 'visual' ? (
        <div
          ref={quillWrapRef}
          className="estab-descricao-quill [&_.ql-toolbar]:border-gray-200 [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-t-0 [&_.ql-container]:border-gray-200 [&_.ql-container]:border-x-0 [&_.ql-container]:border-b-0 [&_.ql-editor]:min-h-[220px] [&_.ql-editor]:text-sm"
        >
          <ReactQuill
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={[...FORMATS]}
            placeholder={placeholder}
          />
        </div>
      ) : (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-h-[220px] resize-y border-0 px-3 py-2 font-mono text-xs text-gray-800 focus:ring-2 focus:ring-orange-400 focus:outline-none"
          spellCheck={false}
          aria-label="Descrição em HTML"
        />
      )}
      <p className="border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] text-gray-500">
        Colar só texto: <kbd className="rounded bg-gray-200 px-1">Ctrl</kbd>+<kbd className="rounded bg-gray-200 px-1">Shift</kbd>+<kbd className="rounded bg-gray-200 px-1">V</kbd>
      </p>
    </div>
  );
}
